/**
 * Мета-теги страницы одним вызовом.
 *
 * До этого на сайте был только useSeoMeta с title и description на главной:
 * в превью ссылки мессенджеры показывали голый заголовок без картинки, а
 * поисковики — без canonical и структурированных данных.
 *
 * Абсолютные адреса обязательны: og:url и og:image относительными путями
 * краулеры не разбирают, поэтому всё разворачивается через publicUrl.
 */

const FALLBACK_ORIGIN = 'https://adfcapital.ru'

/** Картинка превью по умолчанию — 1200×630, как ждут OG и Twitter. */
export const DEFAULT_OG_IMAGE = '/og/adfcapital.png'

/**
 * Превью на других языках.
 *
 * В самих картинках набран текст — заголовок, плашки, логотип, — и английская
 * страница, отданная в мессенджер, показывала русское превью. Страницы
 * по-прежнему передают русский путь, подмена происходит здесь. Своя обложка
 * статьи сюда не попадает — её путь в списке не значится.
 */
const OG_ПО_ЯЗЫКАМ: Record<string, Record<string, string>> = {
  en: {
    '/og/adfcapital.png': '/og/en/adfcapital.png',
    '/og-blog.png': '/og/en/blog.png',
  },
}

/**
 * Заголовок и описание бывают вычисляемыми: у юридических документов они
 * приходят из Strapi уже после первого рендера.
 */
type SeoText = MaybeRefOrGetter<string>

export type AdfSeoOptions = {
  /** Заголовок без названия сайта: суффикс добавляет titleTemplate. */
  title: SeoText
  description: SeoText
  /** Канонический путь. По умолчанию — текущий маршрут без query. */
  path?: string
  /** Своя картинка превью: относительный путь от корня или абсолютный URL. */
  image?: string
  type?: 'website' | 'article'
  /** Служебные страницы вроде формы регистрации из индекса убираем. */
  noindex?: boolean
  /**
   * У страницы нет перевода: под /en отдаётся тот же русский текст.
   *
   * Тогда английский адрес объявляет каноническим русский и не называет себя
   * английской версией. Иначе поисковик получает две одинаковые страницы,
   * одна из которых помечена как английская, хотя текст в ней русский.
   */
  onlyDefaultLocale?: boolean
}

/** Столько знаков описания поисковик показывает в выдаче; остальное режет сам. */
const DESCRIPTION_LIMIT = 160

/**
 * Описание не длиннее лимита, обрезанное по слову.
 *
 * У статей и документов описание собирается из текста и бывает вдвое длиннее.
 * Поисковик обрежет его посреди слова; режем сами — по границе слова и с
 * многоточием. Короткие описания не трогаем.
 */
export const clampDescription = (value: string, limit = DESCRIPTION_LIMIT): string => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim()
  if (text.length <= limit) return text

  const cut = text.slice(0, limit - 1)
  const lastSpace = cut.lastIndexOf(' ')
  const body = (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:!?—–-]+$/, '')

  return `${body}…`
}

export const useSiteOrigin = () => {
  const config = useRuntimeConfig()
  const raw = String(config.public.publicUrl || FALLBACK_ORIGIN).trim()

  return raw.replace(/\/+$/, '')
}

export const absoluteUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path
  return `${useSiteOrigin()}${path.startsWith('/') ? path : `/${path}`}`
}

export const useAdfSeo = (options: AdfSeoOptions) => {
  const route = useRoute()

  const { t, locale, locales } = useI18n()

  /*
   * Язык по умолчанию берём из конфига модуля, а не из composer'а: там это
   * поле есть не во всех версиях, и опечатка в нём тихо развернулась бы в
   * лишний префикс у русских адресов.
   */
  const i18nConfig = useRuntimeConfig().public.i18n as { defaultLocale?: string } | undefined
  const defaultLocale = i18nConfig?.defaultLocale ?? 'ru'

  /**
   * Путь с префиксом языка. Стратегия prefix_except_default, поэтому у
   * русской версии префикса нет, у остальных — есть.
   */
  const withLocale = (code: string, plain: string) =>
    code === defaultLocale ? plain : `/${code}${plain === '/' ? '' : plain}`

  /*
   * Страницы передают путь строкой («/», «/global-arbitrage»), и без языка
   * английская версия объявляла каноническим адрес русской — поисковик читал
   * её как дубль и не индексировал. Путь из маршрута префикс уже несёт, его
   * не трогаем.
   */
  const routePathWithoutLocale = route.path.replace(new RegExp(`^/${locale.value}(?=/|$)`), '') || '/'

  const plainPath = options.path ?? routePathWithoutLocale
  const localizedPath = options.path === undefined ? route.path : withLocale(locale.value, options.path)

  // Без перевода каноническим остаётся адрес основного языка
  const path = options.onlyDefaultLocale ? withLocale(defaultLocale, plainPath) : localizedPath

  const canonical = absoluteUrl(path)
  const исходная = options.image ?? DEFAULT_OG_IMAGE
  const image = absoluteUrl(OG_ПО_ЯЗЫКАМ[locale.value]?.[исходная] ?? исходная)

  const title = () => toValue(options.title)
  const description = () => clampDescription(toValue(options.description))

  useSeoMeta({
    title,
    description,

    ogTitle: title,
    ogDescription: description,
    ogType: options.type ?? 'website',
    ogUrl: canonical,
    ogSiteName: () => t('seo.siteName'),
    ogLocale: () => (locale.value === 'en' ? 'en_US' : 'ru_RU'),
    ogImage: image,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageType: 'image/png',
    // Альт — сам заголовок: приписывать к нему название компании нельзя,
    // на главной оно уже входит в заголовок и удваивалось.
    ogImageAlt: title,

    // summary_large_image — крупная карточка. Её же читает часть мессенджеров,
    // когда OG-разметки им мало.
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: image,

    robots: options.noindex ? 'noindex, follow' : 'index, follow',
  })

  /*
   * Языковые версии связываем hreflang'ом. Без него у поисковика две похожие
   * страницы на разных адресах и никакого указания, что это одна страница на
   * двух языках. x-default ведёт на русскую: она основная.
   */
  useHead({
    link: [
      { rel: 'canonical', href: canonical },
      ...locales.value
        // У страницы без перевода других языковых версий нет
        .filter(item => !options.onlyDefaultLocale || (typeof item === 'string' ? item : item.code) === defaultLocale)
        .map(item => ({
          rel: 'alternate',
          hreflang: typeof item === 'string' ? item : (item.language ?? item.code),
          href: absoluteUrl(withLocale(typeof item === 'string' ? item : item.code, plainPath)),
        })),
      { rel: 'alternate', hreflang: 'x-default', href: absoluteUrl(withLocale(defaultLocale, plainPath)) },
    ],
  })

  return { canonical, image }
}

/**
 * JSON-LD отдельным блоком: useSeoMeta структурированные данные не умеет,
 * а сериализовать объект в скрипт руками на каждой странице — лишний повод
 * забыть про экранирование.
 *
 * Принимает и готовое значение, и функцию: имя организации и язык зависят
 * от локали, а вычислить их до выбора языка нельзя.
 */
type JsonLdData = Record<string, unknown> | Record<string, unknown>[]

export const useJsonLd = (data: JsonLdData | (() => JsonLdData)) => {
  /*
   * Функцию вычисляем сразу, а не отдаём в useHead как реактивное значение:
   * у каждой локали свой адрес, при переключении языка страница монтируется
   * заново, и пересчитывать блок по ходу жизни компонента незачем.
   */
  const resolved = typeof data === 'function' ? data() : data

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(resolved),
      },
    ],
  })
}
